import { useCallback, useRef, useState } from "react";

/* ── Long-press hook for touch devices ───────────────────── */
export function useLongPress(
  onLongPress: (position: { x: number; y: number }) => void,
  { delay = 500 }: { delay?: number } = {},
) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const posRef = useRef({ x: 0, y: 0 });
  const didLongPress = useRef(false);
  const [pressing, setPressing] = useState(false);

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      // Prevent text selection during long-press
      e.preventDefault();
      const touch = e.touches[0];
      posRef.current = { x: touch.clientX, y: touch.clientY };
      didLongPress.current = false;
      setPressing(true);
      timerRef.current = setTimeout(() => {
        didLongPress.current = true;
        setPressing(false);
        onLongPress({ x: touch.clientX, y: touch.clientY });
      }, delay);
    },
    [onLongPress, delay],
  );

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!timerRef.current) return;
    const touch = e.touches[0];
    const dx = Math.abs(touch.clientX - posRef.current.x);
    const dy = Math.abs(touch.clientY - posRef.current.y);
    // Cancel if finger moves more than 10px (scrolling)
    if (dx > 10 || dy > 10) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      setPressing(false);
    }
  }, []);

  const onTouchEnd = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setPressing(false);
  }, []);

  return { onTouchStart, onTouchMove, onTouchEnd, didLongPress, pressing };
}
